import { apiFetch } from "./client";

/**
 * A-Block A3 — BusinessProfile Gate v2 (type-aware) client.
 *
 * Read: GET /Custom/frontend/clients/:holdingId/business-profile (holding-grain).
 * Breeds: GET /Custom/frontend/breeds?species=dog|cat (curated reference).
 * Write: POST /CClientBusinessProfile/:id/quick-form-save (by profileId +
 *        versionNumber) — soft-save (save_draft) vs hard-gate (save_and_mark_reviewed).
 */

export type GateBranch = "commercial_non_breeder" | "breeder";

export interface BreedRef {
  id: string;
  species: "dog" | "cat";
  nameUa: string;
  nameEn?: string;
}

export interface BusinessProfileForm {
  exists: boolean;
  holdingId: string;
  profileId: string | null;
  versionNumber: number | null;
  status: string;
  branch: GateBranch;
  fields: Record<string, unknown>;
  completeness: { requiredFilled: number; requiredTotal: number; percent: number };
  missingFieldCodes: string[];
  missingFieldLabels: string[];
  allowedActions: {
    canViewBusinessProfile: boolean;
    canEditBusinessProfile: boolean;
    canSaveBusinessProfileDraft: boolean;
    canMarkBusinessProfileReviewed: boolean;
  };
}

export function fetchBusinessProfile(
  holdingId: string,
  signal?: AbortSignal,
): Promise<BusinessProfileForm> {
  return apiFetch<BusinessProfileForm>(
    `/Custom/frontend/clients/${encodeURIComponent(holdingId)}/business-profile`,
    { signal },
  );
}

export function fetchBreeds(
  species: "dog" | "cat",
  signal?: AbortSignal,
): Promise<{ items: BreedRef[]; total: number }> {
  return apiFetch(`/Custom/frontend/breeds`, { query: { species }, signal });
}

export interface BrandRef {
  biBrandId: string;
  name: string;
  inactive?: boolean;
}

/**
 * KormaxBrand picker (dealer scenario). Pass saved refs so since-deactivated
 * brands still resolve (rendered as inactive, not selectable as new).
 */
export function fetchBrands(
  refs?: string[],
  signal?: AbortSignal,
): Promise<{ items: BrandRef[]; total: number }> {
  return apiFetch(`/Custom/frontend/brands`, {
    query: refs && refs.length ? { refs: refs.join(",") } : undefined,
    signal,
  });
}

export interface QuickFormSavePayload {
  versionNumber: number;
  stepData: Record<string, Record<string, unknown>>;
  action: "save_draft" | "save_and_mark_reviewed";
}

export function quickFormSave(
  profileId: string,
  payload: QuickFormSavePayload,
  signal?: AbortSignal,
): Promise<unknown> {
  return apiFetch(`/CClientBusinessProfile/${encodeURIComponent(profileId)}/quick-form-save`, {
    method: "POST",
    body: payload,
    signal,
  });
}
